/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../../../lib/apiClient';

export const getCaseInformation = createAsyncThunk(
  'casesNewMexico/getCaseInformation',
  async (id) => {
    const { data } = await apiClient.get(`/api/newMexicoCases/${id}/caseInformation`);
    return data;
  },
);

export const getNewMexicoLegalAssistants = createAsyncThunk(
  'casesNewMexico/getNewMexicoLegalAssistants',
  async (id) => {
    const { data } = await apiClient.get(`/api/newMexicoCases/${id}/legalAssistants`);
    return data.result.map(({ userId, firstName, lastName }) => {
      return {
        id: userId,
        label: `${firstName} ${lastName}`,
        value: userId,
      };
    });
  },
);

export const updateNoticeRequest = createAsyncThunk(
  'casesNewMexico/updateNoticeRequest',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.put(`/api/newMexicoCases/${id}/noticeRequest`, body);
      return data;
    } catch (e) {
      return rejectWithValue(e?.response?.data);
    }
  },
);

export const updateEvictionRequest = createAsyncThunk(
  'casesNewMexico/updateEvictionRequest',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.put(`/api/newMexicoCases/${id}/evictionRequest`, body);
      return data;
    } catch (e) {
      return rejectWithValue(e?.response?.data);
    }
  },
);

export const getCaseProcedularHistoryEntries = createAsyncThunk(
  'casesNewMexico/getCaseProcedularHistoryEntries',
  async (id) => {
    const { data } = await apiClient.get(`/api/newMexicoCases/${id}/proceduralHistoryEntries`);
    return data;
  },
);

export const addCaseProceduralHistoryEntry = createAsyncThunk(
  'casesNewMexico/addCaseProceduralHistoryEntry',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post(
        `/api/newMexicoCases/${id}/proceduralHistoryEntries`,
        body,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const updateCaseProceduralHistoryEntry = createAsyncThunk(
  'casesNewMexico/addCaseProceduralHistoryEntry',
  async ({ id, proceduralHistoryEntryId, body }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.put(
        `/api/newMexicoCases/${id}/proceduralHistoryEntries/${proceduralHistoryEntryId}`,
        body,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const removeCaseProceduralHistoryEntry = createAsyncThunk(
  'casesNewMexico/removeCaseProceduralHistoryEntry',
  async ({ id, proceduralEntryId }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.delete(
        `/api/newMexicoCases/${id}/proceduralHistoryEntries/${proceduralEntryId}`,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getListOfAllStatusesForCase = createAsyncThunk(
  'casesNewMexico/getListOfAllStatusesForCase',
  async () => {
    const { data } = await apiClient.get(`/api/newMexicoCases/statuses?OnlyManual=true`);
    return data;
  },
);

export const getStatusesForCase = createAsyncThunk(
  'casesNewMexico/getStatusesForCase',
  async (id) => {
    const { data } = await apiClient.get(`/api/newMexicoCases/${id}/statuses`);

    const caseStatusToRevert = data?.result?.find((item) => {
      const isSystem = item.updatedBy.trim() === 'system';
      const isArchived = item.status === 'Archived';
      return isArchived || !isSystem;
    });

    let statusObj = null;
    if (caseStatusToRevert) {
      statusObj = await apiClient.get(`/api/newMexicoCases/${id}/statuses/canRevert`);
    }

    const adoptedStatuses = data?.result.map((item) => {
      if (item.caseStatusId === caseStatusToRevert?.caseStatusId) {
        return {
          ...item,
          caseId: id,
          mayBeReverted: statusObj?.data?.result,
        };
      }
      return { ...item, item, mayBeReverted: false };
    });

    return adoptedStatuses;
  },
);

export const addStatusToCase = createAsyncThunk(
  'casesNewMexico/addStatusToCase',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post(`/api/newMexicoCases/${id}/statuses`, body);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const createCasesFromBulk = createAsyncThunk(
  'casesNewMexico/createCasesFromBulk',
  async (data, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('DateServed', data.DateServed);
      formData.append('File', data.File || new Blob(), data.File?.name || '');
      const { data: resData } = await apiClient.post(`/api/newMexicoCases/bulk`, formData);

      return resData;
    } catch (error) {
      const errorStatus = error.response.status;
      if (errorStatus === 400) {
        return rejectWithValue(error.response.data.errorMessage);
      }
      return rejectWithValue(error.response.data.errorMessage);
    }
  },
);

export const printNewMexicoCaseDocument = createAsyncThunk(
  'casesGeneral/printNewMexicoCaseDocument',
  async (args, { rejectWithValue }) => {
    try {
      const { id, ...body } = args;
      const { data } = await apiClient.post(`/api/newMexicoCases/${id}/documents/print`, body);

      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getNewMexicoRelatedCases = createAsyncThunk(
  'casesNewMexico/getNewMexicoRelatedCases',
  async ({ caseId }) => {
    const { data } = await apiClient.get(`/api/newMexicoCases/${caseId}/relativeCases`);
    return data;
  },
);

export const deleteLastNewMexicoCaseStatus = createAsyncThunk(
  'casesNewMexico/deleteLastNewMexicoCaseStatus',
  async ({ id }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.delete(`/api/newMexicoCases/${id}/statuses/revert`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const deleteNewMexicoCaseStatusById = createAsyncThunk(
  'casesNewMexico/deleteNewMexicoCaseStatusById',
  async ({ id, caseStatusId }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.delete(
        `/api/newMexicoCases/${id}/statusHistoryEntries/${caseStatusId}`,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getNewMexicoNoticeTemplateData = createAsyncThunk(
  'casesNewMexico/getNewMexicoNoticeTemplateData',
  async ({ id, templateId }) => {
    const {
      data: { result },
    } = await apiClient.get(`/api/newMexicoCases/${id}/templates/${templateId}/fields`);

    // const result = {
    //   fields: [
    //     {
    //       label: 'Exemption request',
    //       key: 'ExemptionRequest',
    //       type: 'checkbox',
    //       required: true,
    //       value: false,
    //     },
    //     {
    //       label: 'Public Sale Date',
    //       key: 'PublicSaleDate',
    //       type: 'date',
    //       required: true,
    //       value: null,
    //     },
    //     {
    //       label: 'Public Sale Time',
    //       key: 'PublicSaleTime',
    //       type: 'time',
    //       required: true,
    //       value: null,
    //     },
    //     {
    //       label: 'Public Sale Lot Number',
    //       key: 'PublicSaleLotNum',
    //       type: 'int',
    //       required: true,
    //       value: null,
    //     },
    //     {
    //       label: 'Minimum bid for the items',
    //       key: 'PublicSaleSum',
    //       type: 'float',
    //       required: true,
    //       value: 0.0,
    //     },
    //   ],
    // };
    return {
      id,
      templateId,
      fields: result.fields,
    };
  },
);

export const createNewMexicoDocument = createAsyncThunk(
  'casesNewMexico/createDocument',
  async ({ id, templateId, ...body }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post(
        `/api/newMexicoCases/${id}/documents/${templateId}`,
        body,
      );

      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const initialState = {
  getCaseInformation: null,
  getCaseInformationError: null,
  updateNoticeRequest: null,
  updateNoticeRequestError: null,
  updateEvictionRequest: null,
  updateEvictionRequestError: null,
  getCaseProcedularHistoryEntries: null,
  getCaseProcedularHistoryEntriesError: null,
  addCaseProceduralHistoryEntry: null,
  addCaseProceduralHistoryEntryError: null,
  updateCaseProceduralHistoryEntryError: null,
  getListOfAllStatusesForCase: [],
  getListOfAllStatusesForCaseError: null,
  getStatusesForCase: null,
  getStatusesForCaseError: null,
  addStatusToCase: null,
  addStatusToCaseError: null,
  error: null,
  relatedNewMexicoCases: null,
  legalAssistants: [],
  noticeTemplateData: null,
};

export const casesNewMexicoSlice = createSlice({
  name: 'casesNewMexico',
  initialState,
  reducers: {},
  extraReducers: {
    [getNewMexicoRelatedCases.fulfilled]: (state, action) => {
      state.relatedNewMexicoCases = action.payload.result;
    },
    [getCaseInformation.fulfilled]: (state, action) => {
      state.getCaseInformation = action.payload;
    },
    [getCaseInformation.rejected]: (state, action) => {
      state.getCaseInformationError = action.payload;
    },
    [updateNoticeRequest.fulfilled]: (state, action) => {
      state.updateNoticeRequest = action.payload;
    },
    [updateNoticeRequest.rejected]: (state, action) => {
      state.updateNoticeRequestError = action.payload;
    },
    [updateEvictionRequest.fulfilled]: (state, action) => {
      state.updateEvictionRequest = action.payload;
    },
    [updateEvictionRequest.rejected]: (state, action) => {
      state.updateEvictionRequestError = action.payload;
    },
    [getCaseProcedularHistoryEntries.fulfilled]: (state, action) => {
      state.getCaseProcedularHistoryEntries = action.payload;
    },
    [getCaseProcedularHistoryEntries.rejected]: (state, action) => {
      state.getCaseProcedularHistoryEntriesError = action.payload;
    },
    [addCaseProceduralHistoryEntry.fulfilled]: (state, action) => {
      state.addCaseProceduralHistoryEntry = action.payload;
    },
    [addCaseProceduralHistoryEntry.rejected]: (state, action) => {
      state.addCaseProceduralHistoryEntryError = action.payload;
    },
    [updateCaseProceduralHistoryEntry.rejected]: (state, action) => {
      state.updateCaseProceduralHistoryEntryError = action.payload;
    },
    [getListOfAllStatusesForCase.fulfilled]: (state, action) => {
      state.getListOfAllStatusesForCase = action.payload.result.map(({ value }) => ({
        id: value,
        label: value,
        value,
      }));
    },
    [getListOfAllStatusesForCase.rejected]: (state, action) => {
      state.getListOfAllStatusesForCaseError = action.payload;
    },
    [getStatusesForCase.fulfilled]: (state, action) => {
      state.getStatusesForCase = action.payload;
    },
    [getStatusesForCase.rejected]: (state, action) => {
      state.getStatusesForCaseError = action.payload;
    },
    [addStatusToCase.fulfilled]: (state, action) => {
      state.addStatusToCase = action.payload;
    },
    [addStatusToCase.rejected]: (state, action) => {
      state.addStatusToCaseError = action.payload;
    },
    [printNewMexicoCaseDocument.rejected]: (state, action) => {
      state.error = action.payload;
    },
    [createNewMexicoDocument.rejected]: (state, action) => {
      state.error = action.payload;
    },
    [getNewMexicoLegalAssistants.fulfilled]: (state, action) => {
      state.legalAssistants = action.payload;
    },
    [getNewMexicoNoticeTemplateData.fulfilled]: (state, action) => {
      state.noticeTemplateData = action.payload;
    },
  },
});

export default casesNewMexicoSlice.reducer;
